'use client';

import { useQuery } from '@tanstack/react-query';
import apiService from '../services/api';
import { FOUND_OBJECT_STATS_QUERY_KEY } from '../lib/messagingQueryCache';
import { FOUND_OBJECT_STATS_POLL_MS } from '../lib/pollingConstants';
import { resolvePollIntervalMs, type PollingEndpointKey } from '../lib/pollingManager';
import type { FoundObjectStats } from '../types/foundObject';
import { usePollingGate } from './usePollingGate';

const ENDPOINT_KEY: PollingEndpointKey = 'found-object-stats';

type Options = {
  enabled?: boolean;
  poll?: boolean;
};

async function fetchFoundObjectStats(): Promise<FoundObjectStats> {
  const res = await apiService.getFoundObjectStats();
  const raw = res.data as FoundObjectStats & { data?: FoundObjectStats };
  return (raw?.data ?? raw) as FoundObjectStats;
}

export function useFoundObjectStatsQuery(options?: Options) {
  const enabled = options?.enabled ?? true;
  const poll = options?.poll ?? true;
  const { allowPolling } = usePollingGate();

  return useQuery({
    queryKey: FOUND_OBJECT_STATS_QUERY_KEY,
    queryFn: fetchFoundObjectStats,
    enabled,
    retry: 0,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchInterval: resolvePollIntervalMs(
      poll ? FOUND_OBJECT_STATS_POLL_MS : false,
      ENDPOINT_KEY,
      allowPolling,
    ),
    meta: { pollingEndpoint: ENDPOINT_KEY },
  });
}
